import { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { ShoppingCart, Edit2, Trash2, Package, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input } from '@caruso/ui';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';

interface ProductImage {
  url: string;
  isPrimary?: boolean;
}

interface Product {
  id: string;
  name: string;
  price: number;
  category?: string;
  images?: ProductImage[];
}

interface QuickSaleData {
  productId?: string;
  productName?: string;
  productPrice?: number;
  quickSaleMessage?: string;
}

export const QuickSaleNode = memo(({ data, id }: NodeProps<QuickSaleData>) => {
  const { deleteElements } = useReactFlow();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isEditing, setIsEditing] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productId, setProductId] = useState(data.productId || '');
  const [productName, setProductName] = useState(data.productName || '');
  const [productPrice, setProductPrice] = useState(data.productPrice || 0);
  const [quickSaleMessage, setQuickSaleMessage] = useState(data.quickSaleMessage || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isProductModalOpen) {
      loadProducts();
    }
  }, [isProductModalOpen, search]);

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
      });

      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/products?${params}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load products');

      const responseData = await response.json();
      const productsList = responseData.data || responseData;
      setProducts(Array.isArray(productsList) ? productsList : []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erro ao carregar produtos');
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSelectProduct = (product: Product) => {
    setProductId(product.id);
    setProductName(product.name);
    setProductPrice(product.price);
    setIsProductModalOpen(false);
  };

  const handleClearProduct = () => {
    setProductId('');
    setProductName('');
    setProductPrice(0);
  };

  const handleSave = () => {
    if (!productId || !productName) {
      toast.error('Selecione um produto');
      return;
    }

    data.productId = productId;
    data.productName = productName;
    data.productPrice = productPrice;
    data.quickSaleMessage = quickSaleMessage;

    setIsEditing(false);
    toast.success('Nó de venda rápida configurado');
  };

  const handleCancel = () => {
    setProductId(data.productId || '');
    setProductName(data.productName || '');
    setProductPrice(data.productPrice || 0);
    setQuickSaleMessage(data.quickSaleMessage || '');
    setIsEditing(false);
  };

  return (
    <>
      <div className="bg-neutral-900 border-2 border-brand-gold/40 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-brand-gold" />

        <div className="flex items-center gap-2 mb-3">
          <ShoppingCart className="w-5 h-5 text-green-400" />
          <span className="font-bold text-white">Venda Rápida</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-brand-gold hover:text-white transition-colors p-1"
              title="Editar venda"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {productName ? (
            <>
              <div className="flex items-center gap-2 p-2 bg-neutral-800 rounded">
                <Package className="w-4 h-4 text-brand-gold" />
                <span className="flex-1 text-sm text-brand-ivory font-semibold">{productName}</span>
              </div>
              {quickSaleMessage && (
                <div className="text-brand-ivory/80 text-sm whitespace-pre-wrap p-2 bg-neutral-800 rounded">
                  {quickSaleMessage}
                </div>
              )}
            </>
          ) : (
            <div className="text-brand-ivory/50 text-sm text-center py-3">
              Clique para selecionar um produto
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-brand-gold" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-brand-gold/20 text-white max-w-4xl w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-400" />
              Configurar Venda Rápida
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Selecione um produto e personalize a mensagem
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Product Selector */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                Produto *
              </label>
              {productName ? (
                <div className="flex items-center gap-2 p-3 bg-neutral-800/50 border border-neutral-700 rounded-lg">
                  <Package className="w-5 h-5 text-brand-gold flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{productName}</p>
                    <p className="text-xs text-brand-ivory/70">R$ {productPrice.toFixed(2)}</p>
                  </div>
                  <button
                    onClick={handleClearProduct}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                    title="Remover produto"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => setIsProductModalOpen(true)}
                  variant="outline"
                  className="w-full justify-start border-neutral-700 text-white hover:bg-neutral-800"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Pesquisar produto
                </Button>
              )}
            </div>

            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                Mensagem com Template (opcional)
              </label>
              <textarea
                value={quickSaleMessage}
                onChange={(e) => setQuickSaleMessage(e.target.value)}
                className="w-full p-4 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold/50 font-mono text-base"
                rows={8}
                placeholder="Exemplo: Gostaria de ofertar esse produto {{nome_produto}} que está muito bom! Se te interessar, clique no link: {{link_compra}}"
              />
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-brand-gold">Variáveis disponíveis:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{nome_produto}}'} - Nome do produto
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{preco_produto}}'} - Preço formatado
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{link_compra}}'} - Link de venda
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{nome_cliente}}'} - Nome do cliente
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pesquisa de Produtos */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="bg-neutral-900 border-brand-gold/20 text-white w-[95vw] max-w-[3000px] max-h-[85vh] flex flex-col p-0 gap-0">
          <div className="p-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-brand-gold" />
                Selecionar Produto
              </DialogTitle>
              <DialogDescription className="text-brand-ivory/70">
                Pesquise e selecione o produto para venda rápida
              </DialogDescription>
            </DialogHeader>

            {/* Search Input */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
              <Input
                placeholder="Pesquisar produtos..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
              />
            </div>
          </div>

          {/* Products List - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 min-h-0">
            <div className="space-y-2 pb-4">
              {isLoadingProducts ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  Carregando produtos...
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  Nenhum produto encontrado
                </div>
              ) : (
                products.map((product) => {
                  const primaryImage = product.images?.find(img => img.isPrimary) || product.images?.[0];
                  return (
                    <button
                      key={product.id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full flex items-center gap-4 p-4 bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 hover:border-brand-gold/50 rounded-lg transition-all text-left group"
                    >
                      {primaryImage ? (
                        <div className="w-16 h-16 flex-shrink-0 rounded border border-neutral-700 overflow-hidden bg-neutral-700">
                          <img
                            src={primaryImage.url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Replace with placeholder on error
                              e.currentTarget.style.display = 'none';
                              const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full hidden items-center justify-center">
                            <Package className="w-7 h-7 text-brand-gold/50" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-neutral-700 rounded border border-neutral-600 flex-shrink-0">
                          <Package className="w-7 h-7 text-brand-gold/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-medium text-white group-hover:text-brand-gold transition-colors">
                          {product.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <p className="text-sm text-brand-gold font-semibold">
                            R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          {product.category && (
                            <span className="text-sm text-brand-ivory/50">
                              • {product.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="p-6 pt-4 border-t border-neutral-700 flex justify-end">
            <Button
              onClick={() => setIsProductModalOpen(false)}
              variant="outline"
              className="border-neutral-700 text-white hover:bg-neutral-800"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

QuickSaleNode.displayName = 'QuickSaleNode';
